'use client';

import { c as _c } from "react/compiler-runtime";
import { useGrowthBook } from '@growthbook/growthbook-react';
import { createContext, type PropsWithChildren, use, useState } from 'react';
import { useAppExperienceAvailableDates } from '../_hooks/use-app-experience-available-dates';
import { useExperienceFilterContext } from './app-experience-filter-provider';
const EDP_DATE_SELECTION_FLAG = 'ab-edp-date-selection';
const ArmContext = createContext({
  isVariant: false
});
export function useEdpDateSelectionArm(): {
  isVariant: boolean;
} {
  return use(ArmContext);
}

// Evaluates the flag only after the latch, without changing the element tree (a swap
// would remount the whole EDP). `useFeature` is just `evalFeature` on the context
// instance; "use no memo" keeps the React Compiler from caching the evaluation.
function useArmIsVariant(latched: boolean) {
  'use no memo';

  const growthBook = useGrowthBook();
  return latched && growthBook.evalFeature<string>(EDP_DATE_SELECTION_FLAG).value === 'variant';
}

// A/B test FR7-4349: the flag may only be evaluated (and `experiment_viewed` fire) on
// EDPs that are not tickets and have available dates. Once that holds it stays true
// for the page's lifetime, so a later empty date list does not flip the arm.
export function EdpDateSelectionArmProvider(t0) {
  const $ = _c(5);
  const {
    children
  } = t0;
  const {
    experience,
    filter
  } = useExperienceFilterContext();
  const isTicket = experience.experienceType === "ticket";
  const {
    availableDates
  } = useAppExperienceAvailableDates(experience.publicId, filter.selectedVariant, filter.selectedOptions, !isTicket);
  const [latched, setLatched] = useState(false);
  if (!latched && !isTicket && !!availableDates?.length) {
    setLatched(true);
  }
  const isVariant = useArmIsVariant(latched);
  let t1;
  if ($[0] !== isVariant) {
    t1 = {
      isVariant
    };
    $[0] = isVariant;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <ArmContext value={t1}>{children}</ArmContext>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}